import { Icons } from "assets";
import classNames from "classnames";
import moment from "moment";
import { useEffect, useId, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Draggable from "shared/components/common/customDraggable";
import { setBreadCrumb } from "shared/redux/reducers/breadCrumbSlice";
import { routeConstant } from "shared/routes/routeConstant";
import { CGPColumnHeads } from "shared/utils/constants";
import { JobRole } from "shared/utils/enum";
import { sortJobsByKey } from "shared/utils/helper";
import styles from "./style.module.scss";

const MultipleJobsColumnHeads: CustomTableHeadType[] = [
  {
    title: "Job Title",
    key: "current_version.jobTitle",
  },
  {
    title: "Department",
    key: "current_version.department",
  },
  {
    title: "Job Code",
    key: "jobCode",
  },
  {
    title: "Job Grade",
    key: "jobGrade",
  },
  {
    title: "Role Type",
    key: "roleType",
  },
  {
    title: "Level of Work & Modifier",
    key: "current_version.level_of_work.short_des",
  },
  {
    title: "Institutional Expertise & Skill",
    key: "current_version.q2.short_des",
  },
  {
    title: "Cultural Impact",
    key: "current_version.q3.short_des",
  },
  {
    title: "Physical Environment",
    key: "current_version.q4.short_des",
  },
  {
    title: "Mental & Emotional Environment",
    key: "current_version.q5.short_des",
  },
  {
    title: "Application of Knowledge & Skill",
    key: "current_version.q6.short_des",
  },
  {
    title: "Manual Effort",
    key: "current_version.q7.short_des",
  },
  {
    title: "Outcomes",
    key: "current_version.q8.short_des",
  },
  {
    title: "RAS Score",
    key: "current_version.rasScore",
  },
  {
    title: "RAS Level",
    key: "current_version.rasLevel",
  },
  {
    title: "RAS Code",
    key: "current_version.shortCode",
  },
  {
    title: "Evaluated By",
    key: "evaluated_by.name",
  },
  {
    title: "Last Date Modified",
    key: "created_at",
  },
];

const MulitpleEvaluationsDetails = () => {
  const {
    evaluations: { evaluations },
  } = useSelector((state: any) => state.root);
  const dispatch = useDispatch();
  const [localEvaluations, seLocaltEvaluations] = useState<any[]>([]);
  const [sortingKeys, setSortingKeys] = useState<string[]>([]);

  const handleSorting = (key: string, revese = false) => {
    let keys = [...sortingKeys];
    if (keys.includes(key)) {
      keys = keys.filter((item) => item !== key);
    } else {
      keys.push(key);
    }
    setSortingKeys(keys);
    let jobs = [...evaluations];
    sortJobsByKey(jobs, key, revese);
    seLocaltEvaluations(jobs);
  };

  useEffect(() => {
    dispatch(
      setBreadCrumb({
        crumbs: [
          {
            title: routeConstant.multipleEvaluationRecordDetail.title,
          },
        ],
      })
    );

    seLocaltEvaluations(evaluations);

    // eslint-disable-next-line
  }, []);

  return (
    <div className={classNames(styles.mainContainer)}>
      <div
        className={classNames(
          "flex items-start gap-3 lg:items-center justify-between flex-col lg:flex-row px-4 py-4"
        )}
      >
        <div className={classNames("flex flex-col items-start")}>
          <h1 className={classNames(styles.tableMainHeading)}>
            Evaluation Results with Version History
          </h1>
          <p className={classNames(styles.tableMainTitle)}>
            Showing Evaluation History for Jobs
          </p>
        </div>
      </div>
      <Draggable disableScroll={evaluations.length === 0}>
        <table
          className="table w-full"
          style={{ minWidth: "3627px", margin: "0px" }}
        >
          <tr className={classNames(styles.stickyHeader)}>
            <td colSpan={6}></td>
            <th
              colSpan={3}
              scope="colgroup"
              className={classNames(styles.mainHead)}
            >
              Knowledge & Skill
            </th>
            <th
              colSpan={2}
              scope="colgroup"
              className={classNames(styles.mainHead)}
            >
              Working Environment
            </th>
            <td colSpan={9}></td>
          </tr>

          <tr
            style={{ height: "48px" }}
            className={classNames(styles.stickyHeader2)}
          >
            {MultipleJobsColumnHeads.map((column, index) => {
              return (
                <TableHead
                  index={index}
                  key={index}
                  column={column}
                  sortingKeys={sortingKeys}
                  handleSorting={handleSorting}
                />
              );
            })}
          </tr>
          <tbody>
            {localEvaluations.map((evaluation, index) => {
              return <TableRow item={evaluation} key={evaluation?.id} />;
            })}
          </tbody>
        </table>
      </Draggable>
    </div>
  );
};

const TableHead = ({ index, column, sortingKeys, handleSorting }: any) => {
  const id = useId();

  return (
    <th
      className={classNames(
        (index === 0 || index === CGPColumnHeads.length - 1) && "sticky-col",
        index === 0 && "first-col",
        "ps-2 pe-2",
        styles.tableHead
      )}
      style={index === CGPColumnHeads.length - 1 ? { right: "0px" } : {}}
    >
      <div
        className={classNames(
          "flex items-center  relative",
          index !== CGPColumnHeads.length - 1
            ? "justify-between"
            : "justify-center"
        )}
      >
        {column?.title}
        {index !== CGPColumnHeads.length - 1 ? (
          <div id={`custom-select-head-container-${id}`}>
            {sortingKeys.includes(column?.key) ? (
              <Icons.SortAscending
                className={classNames("cursor-pointer")}
                onClick={() => {
                  handleSorting(column?.key, true);
                }}
              />
            ) : (
              <Icons.SortDescending
                className={classNames("cursor-pointer")}
                onClick={() => {
                  handleSorting(column?.key, false);
                }}
              />
            )}
          </div>
        ) : null}
      </div>
    </th>
  );
};

const TableRow = ({ item }: any) => {
  return (
    <>
      <tr>
        <td
          className={classNames(
            styles.tableContentLabel,
            "ps-2 pe-2 py-3 sticky-col first-col"
          )}
        >
          {item?.current_version?.jobTitle}
        </td>
        <td className={classNames(styles.tableContentLabel, "px-2 py-3")}>
          {item?.current_version?.department}
        </td>
        <td className={classNames(styles.tableContentLabel, "px-2 py-3")}>
          {item?.jobCode}
        </td>
        <td className={classNames(styles.tableContentLabel, "px-2 py-3")}>
          {item?.jobGrade ? item?.jobGrade : "N/A"}
        </td>
        <td className={classNames(styles.tableContentLabel, "px-2 py-3")}>
          {item?.roleType === JobRole.management
            ? "Management"
            : "Individual Contributor"}
        </td>
        <td className={classNames(styles.tableContentLabel, "px-2 py-3")}>
          <div className="flex items-center justify-between gap-3 w-full">
            <label>{item?.current_version?.level_of_work?.short_des}</label>
            <span className={classNames(styles.modifier)}>
              {item?.current_version?.modifier}
            </span>
          </div>
        </td>
        <td className={classNames(styles.tableContentLabel, "px-2 py-3")}>
          {item?.current_version?.q2?.short_des}
        </td>
        <td className={classNames(styles.tableContentLabel, "px-2 py-3")}>
          {item?.current_version?.q3?.short_des}
        </td>
        <td className={classNames(styles.tableContentLabel, "px-2 py-3")}>
          {item?.current_version?.q4?.short_des}
        </td>
        <td className={classNames(styles.tableContentLabel, "px-2 py-3")}>
          {item?.current_version?.q5?.short_des}
        </td>
        <td className={classNames(styles.tableContentLabel, "px-2 py-3")}>
          {item?.current_version?.q6?.short_des}
        </td>
        <td className={classNames(styles.tableContentLabel, "px-2 py-3")}>
          {item?.current_version?.q7?.short_des}
        </td>
        <td className={classNames(styles.tableContentLabel, "px-2 py-3")}>
          {item?.current_version?.q8?.short_des}
        </td>
        <td
          className={classNames(
            styles.tableContentLabel,
            styles.highlighted,
            "px-2 py-3"
          )}
        >
          {item?.current_version?.rasScore}
        </td>
        <td
          className={classNames(
            styles.tableContentLabel,
            styles.highlighted,
            "px-2 py-3"
          )}
        >
          {item?.current_version?.rasLevel}
        </td>
        <td
          className={classNames(
            styles.tableContentLabel,
            styles.highlighted,
            "px-2 py-3"
          )}
        >
          {item?.current_version?.shortCode}
        </td>
        <td className={classNames(styles.tableContentLabel, "px-2 py-3")}>
          {item?.fromAdmin && item?.evaluated_by?.name
            ? `CGP (${item?.evaluated_by?.name})`
            : item?.fromAdmin
            ? "CGP"
            : item?.evaluated_by?.name}
        </td>
        <td className={classNames(styles.tableContentLabel, "px-2 py-3")}>
          {moment(item?.created_at).format("MMM DD, YYYY")}
        </td>
      </tr>
    </>
  );
};

export default MulitpleEvaluationsDetails;
